import type { Theme } from "@mui/material";
import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import StatusChip from "@entrilia/shared/components/StatusChip";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { formatMoney, formatNumber } from "@entrilia/shared/utilities/formatters";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import type {
  CashFlowDetailLine,
  CashFlowLine,
  CashFlowPaymentStatus,
  CashFlowPortalStatus,
  CashFlowUnfundedImpactDirection,
} from "../../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../../context/useClientContext";

const rowWidths = {
  portalStatus: 130,
  name: "20%",
  paymentStatus: 130,
  fund: "auto",
  date: 120,
  amount: 200,
  unfunded: 200,
};

const columnCount = 10;

const getStatusData = (
  paymentStatus: CashFlowPaymentStatus
): { statusText: string; statusColor: (theme: Theme) => string } => {
  switch (paymentStatus) {
    case "Unpaid":
      return { statusText: "Unpaid", statusColor: (t) => t.palette.error.main };
    case "PartlyPaid":
      return { statusText: "Partially Paid", statusColor: (t) => t.palette.warning.main };
    case "Paid":
      return { statusText: "Paid", statusColor: (t) => t.palette.success.main };
    default:
      return { statusText: autoFormatCamelCase(paymentStatus), statusColor: (t) => t.palette.text.secondary };
  }
};

const impactTextMap: Record<CashFlowUnfundedImpactDirection, string> = {
  None: "None",
  Increases: "Increases",
  Decreases: "Decreases",
};

interface Props {
  cashFlows: CashFlowLine[];
  isLoading: boolean;
}

const InvestorCashFlowsTable = ({ cashFlows, isLoading }: Props) => {
  const { investorPortalApp } = useClientContext();

  const [expandedRowIds, setExpandedRowIds] = useState(Object.fromEntries(cashFlows.map((row) => [row.id, false])));

  const handleRowToggle = (id: string) => setExpandedRowIds((rowIds) => ({ ...rowIds, [id]: !rowIds[id] }));

  if (isLoading) {
    return <InlineLoader />;
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell></TableCell>
          <TableCell>Portal Status</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Payment Status (Fund Ops)</TableCell>
          {investorPortalApp && <TableCell>Payment Status (Preview)</TableCell>}
          {investorPortalApp && <TableCell>Payment Status (Live)</TableCell>}
          <TableCell>Fund</TableCell>
          <TableCell width={rowWidths.date}>Due Date</TableCell>
          <TableCell width={rowWidths.amount} align="right">
            Amount
          </TableCell>
          <TableCell width={rowWidths.unfunded} align="right">
            Impact on Unfunded
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {cashFlows.length > 0 ? (
          cashFlows.map((row) => (
            <RowWithDetails
              key={row.id}
              row={row}
              expanded={!!expandedRowIds[row.id]}
              onToggleClick={handleRowToggle}
            />
          ))
        ) : (
          <EmptyDataRow />
        )}
      </TableBody>
    </Table>
  );
};

interface MasterRowProps {
  row: CashFlowLine;
  expanded: boolean;
  onToggleClick: (rowId: string) => void;
}

const MasterRow = ({ row, expanded, onToggleClick }: MasterRowProps) => {
  const { investorPortalApp } = useClientContext();

  const handleToggleButtonClick = () => onToggleClick(row.id);

  const paymentStatusData = getStatusData(row.paymentStatus);
  const paymentStatusDataPreview = getStatusData(row.paymentStatusPreview);
  const paymentStatusDataLive = getStatusData(row.paymentStatusLive);

  return (
    <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
      <TableCell padding="checkbox">
        <IconButton onClick={handleToggleButtonClick}>{expanded ? <CollapseIcon /> : <ExpandIcon />}</IconButton>
      </TableCell>
      <TableCell width={rowWidths.portalStatus}>
        <InvestorPortalStatusTag status={row.portalStatus} />
      </TableCell>
      <TableCell width={rowWidths.name}>{row.name}</TableCell>
      <TableCell width={rowWidths.paymentStatus}>
        <StatusChip label={paymentStatusData.statusText} color={paymentStatusData.statusColor} />
      </TableCell>
      {investorPortalApp && (
        <TableCell width={rowWidths.paymentStatus}>
          <StatusChip label={paymentStatusDataPreview.statusText} color={paymentStatusDataPreview.statusColor} />
        </TableCell>
      )}
      {investorPortalApp && (
        <TableCell width={rowWidths.paymentStatus}>
          <StatusChip label={paymentStatusDataLive.statusText} color={paymentStatusDataLive.statusColor} />
        </TableCell>
      )}
      <TableCell width={rowWidths.fund}>
        <TypographyTooltipEllipsis text={row.fundName} />
      </TableCell>
      <TableCell width={rowWidths.date}>{convertISODate(row.dueDate)}</TableCell>
      <TableCell width={rowWidths.amount} align="right">
        <Typography variant="subtitle2">
          {row.currencyCode
            ? formatMoney(row.amount, row.currencyCode, { currencySign: "accounting" })
            : formatNumber(row.amount)}
        </Typography>
      </TableCell>
      <TableCell width={rowWidths.unfunded} align="right">
        <Typography variant="subtitle2">
          {row.currencyCode
            ? formatMoney(row.impactOnUnfunded, row.currencyCode, { currencySign: "accounting" })
            : formatNumber(row.impactOnUnfunded)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const RowWithDetails = ({ row, expanded, onToggleClick }: MasterRowProps) => (
  <>
    <MasterRow row={row} expanded={expanded} onToggleClick={onToggleClick} />
    <ExpandedWrapper expanded={expanded}>
      {row.details.map((detail) => (
        <DetailRow key={detail.id} detail={detail} />
      ))}
    </ExpandedWrapper>
  </>
);

const DetailRow = ({ detail }: { detail: CashFlowDetailLine }) => {
  return (
    <TableRow
      hover={true}
      sx={(them) => ({
        "&.MuiTableRow-root": {
          borderBottom: `1px dashed ${them.palette.divider}`,
          "&:last-child": {
            borderBottom: 0,
          },
          td: {
            borderBottom: 0,
          },
        },
      })}
    >
      <TableCell width="auto" align="left" sx={{ pl: 2 }}>
        <Typography color="textSecondary">{detail.description}</Typography>
      </TableCell>
      <TableCell width={rowWidths.amount} sx={{ pr: 1 }} align="right">
        <Typography color="textSecondary">
          {formatMoney(detail.amount, detail.currencyCode, { currencySign: "accounting" })}
        </Typography>
      </TableCell>
      <TableCell width={rowWidths.unfunded} sx={{ pr: 1 }} align="right">
        <Typography color="textSecondary">{impactTextMap[detail.impactDirection]}</Typography>
      </TableCell>
    </TableRow>
  );
};

const ExpandedWrapper = ({ expanded, children }: PropsWithChildren<{ expanded: boolean }>) => {
  const { investorPortalApp } = useClientContext();

  const startColumnCount = investorPortalApp ? 6 : 4;

  const cellSx = { p: 0, border: expanded ? "" : "unset" };
  return (
    <TableRow>
      <TableCell sx={cellSx} colSpan={startColumnCount} />
      <TableCell sx={cellSx} colSpan={columnCount - startColumnCount}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mr: 1, my: 0.5 }}>
            <Table>
              <TableBody>{children}</TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const EmptyDataRow = () => {
  return (
    <TableRow key="empty">
      <TableCell colSpan={columnCount} align="center" sx={{ py: 3 }}>
        <Typography color="textSecondary">No data</Typography>
      </TableCell>
    </TableRow>
  );
};

const InvestorPortalStatusTag = ({ status }: { status: CashFlowPortalStatus }) => {
  switch (status) {
    case "None":
      return <StatusChip label="None" withDot color={(t) => t.palette.text.secondary} />;
    case "Preview":
      return <StatusChip label="Preview" withDot color={(t) => t.palette.info.dark} />;
    case "Live":
      return <StatusChip label="Live" withDot color={(t) => t.palette.success.main} />;
    default:
      return null;
  }
};

export default InvestorCashFlowsTable;
